import { Box } from "@mui/material";
import { isNil } from "ramda";
import { useSelector } from "react-redux";
import React from "react";
import ItemsDetailConfigurationViewSection from "../../../common/item/ItemsDetailConfigurationViewSection";
import ItemDetailViewSection, {
  Item,
  ItemDetail,
} from "../../../common/item/ItemDetailViewSection";
import ItemsDetailViewSection, {
  ItemsDetail,
} from "../../../common/item/ItemsDetailViewSection";
import ScrollSpyTabs from "../../../common/nav/ScrollSpyTabs";
import { Data } from "../../../store/terminal/types";
import { Data as MerchantData } from "../../../store/merchant/types";
import { ApplicationState } from "../../../store";
import { Data as CustomGroupData } from "../../../store/customGroup/types";
import useUserAccess from "../../../effects/useUserAccess";
import _, { get } from "lodash";
import {
  EMPTY_ENTITY_ORG_CONFIG,
  EntityOrgConfig,
} from "../../../repo/configuration";

interface TerminalViewDetailProps {
  terminalData: Data;
  merchantData?: any;
  orgConfig?: EntityOrgConfig;
}

const centToDollar = (cents: number) => {
  return (cents / 100).toFixed(2).toString(); // convert cents to dollars and round to 2 decimal places
};

const formatTippingPercentageValue = (tippingMode: string, value: number) => {
  if (value) {
    if (tippingMode === "tip_by_percentage") {
      return value.toString() + "%";
    } else if (tippingMode === "tip_by_value") {
      return "$" + value.toString();
    } else if (value) {
      return value.toString();
    }
  } else {
    return "";
  }
};

const convertTimeTo12Hours = function (timeString: string) {
  return new Date("1970-01-01T" + timeString + "Z").toLocaleTimeString(
    "en-US",
    { timeZone: "UTC", hour12: true, hour: "numeric", minute: "numeric" }
  );
};

const TerminalViewDetail = (props: TerminalViewDetailProps) => {
  const { attributes: at } = props.terminalData;
  const { merchantData } = props;
  const orgConfig = props.orgConfig ?? EMPTY_ENTITY_ORG_CONFIG;
  const hiddenFields = new Set(orgConfig.hiddenFields);
  const hiddenSubsections = new Set(orgConfig.hiddenSubsections);
  const hiddenTabs = new Set(orgConfig.hiddenTabs);
  const viewDefaultValue = orgConfig.defaultValue;

  console.log(at.ReceiptPrint)

  // Display the configured default value for a field when the item's value is
  // null/undefined; otherwise show the item's actual value.
  const displayDefaultValue = (value, fieldName) =>
    isNil(value) ? viewDefaultValue?.[fieldName] : value;

  const filterItems = <T extends { fieldName?: string }>(items: T[]): T[] =>
    hiddenFields.size === 0
      ? items
      : items.filter((i) => !i.fieldName || !hiddenFields.has(i.fieldName));

  const filterItemsData = <
    T extends { sectionName?: string; itemData?: any[] }
  >(
    itemsData: T[]
  ): T[] => {
    if (hiddenFields.size === 0 && hiddenSubsections.size === 0) return itemsData;
    return itemsData
      .filter((s) => !s.sectionName || !hiddenSubsections.has(s.sectionName))
      .map((s) => ({
        ...s,
        itemData: s.itemData ? filterItems(s.itemData) : s.itemData,
      }));
  };
  const merchants: MerchantData[] =
    useSelector((state: ApplicationState) => state.merchant.data?.data) ?? [];

  const access = useUserAccess();
  const SectionAccess = get(access, "Terminal.SectionAccess", {});
  const TerminalInfoAccess = get(SectionAccess, "TerminalInfo.Access", {
    R: false,
    U: false,
  });
  const ProcessorAccess = get(SectionAccess, "Processor.Access", {
    R: false,
    U: false,
  });
  const HostingAccess = get(SectionAccess, "Hosting.Access", {
    R: false,
    U: false,
  });
  const LocationAccess = get(SectionAccess, "Location.Access", {
    R: false,
    U: false,
  });
  const ConfigurationAccess = get(SectionAccess, "Configuration.Access", {
    R: false,
    U: false,
  });
  const OfflineProcessingAccess = get(
    SectionAccess,
    "OfflineProcessing.Access",
    {
      R: false,
      U: false,
    }
  );
  const ReceiptAccess = get(SectionAccess, "Receipt.Access", {
    R: false,
    U: false,
  });
  const SurchargeTippingAccess = get(SectionAccess, "SurchargeTipping.Access", {
    R: false,
    U: false,
  });
  const TerminalKeyAccess = get(SectionAccess, "TerminalKey.Access", {
    R: false,
    U: false,
  });
  const DiningRewardsClubAccess = get(
    SectionAccess,
    "DiningRewardsClub.Access",
    { R: false, U: false }
  );
  const GiftCardAccess = get(SectionAccess, "GiftCard.Access", {
    R: false,
    U: false,
  });
  const PayAtTableAccess = get(SectionAccess, "PayAtTable.Access", {
    R: false,
    U: false,
  });
  // const FixedPaymentsUIAccess = get(SectionAccess, "FixedPaymentsUI.Access", { R: false, U: false });

  const getMerchantNameByMID = (mid: string) => {
    if (mid && merchants && !isNil(merchants)) {
      const merchant = merchants.find(
        (m) => m.attributes.MID?.toLowerCase() === mid.toLowerCase()
      );
      return merchant ? merchant.attributes.Name : null;
    }
    return null;
  };

  const emvOptions =
    useSelector((state: ApplicationState) => state.emv.data) ?? [];

  const emv = emvOptions.find(
    (e) => e.attributes.ConfigName === at.EMVConfigName
  );

  const closeLoopOptions =
    useSelector((state: ApplicationState) => state.closeloop.data) ?? [];

  const closeLoop = closeLoopOptions.find(
    (e) => e.attributes.ConfigName === at.CloseLoopConfigName
  );

  const tokenWalletOptions =
    useSelector((state: ApplicationState) => state.tokenWallet.data) ?? [];

  const tokenWallet = tokenWalletOptions.find(
    (e) => e.attributes.Name === at.TokenWalletFileConfigName
  );

  const customGroups =
    (useSelector<ApplicationState>(
      (store) => store.customGroup.data?.data
    ) as CustomGroupData[]) || [];
  const receipts = useSelector((state: ApplicationState) => state.receipt.data);
  const merchantReceipts =
    receipts && receipts.data
      ? receipts.data
          .filter((data) => data?.attributes?.For === "Merchant")
          .map((i) => i.attributes.Name)
      : [];

  // TM-1264 hide surcharge section for QT850 terminal
  const isHideSurchargeSection = at.TerminalType === "QT 850";

  const customerReceipts =
    receipts && receipts.data
      ? receipts.data
          .filter((data) => data?.attributes?.For === "Customer")
          .map((i) => i.attributes.Name)
      : [];

  const centToDollar = (cents: number) => {
    return (cents / 100).toFixed(2).toString(); // convert cents to dollars and round to 2 decimal places
  };

  // DATA MAPPING
  const terminalInfo: Item[] = [
    { fieldName: "Terminal type", fieldValue: at.TerminalType },
    { fieldName: "Serial number", fieldValue: at.SerNo },
    { fieldName: "TID", fieldValue: at.TID },
    { fieldName: "POI Device ID", fieldValue: at.POIID },
    { fieldName: "Card Prompts", fieldValue: at.CardPrompts },
    { fieldName: "MCC", fieldValue: at.MCC },
    {
      fieldName: "Merchant Name",
      fieldValue: merchantData
        ? merchantData.attributes.Name
        : (getMerchantNameByMID(at.MID as string) as string),
    },
    { fieldName: "Merchant ID", fieldValue: at.MID },
    {
      fieldName: "EMV",
      fieldValue: emv?.attributes.ConfigName || at.EMVConfigName,
    },
    { fieldName: "CPAT Name", fieldValue: at.CPATName },
    {
      fieldName: "Close Loop Config",
      fieldValue: closeLoop?.attributes.ConfigName || at.CloseLoopConfigName,
    },
    {
      fieldName: "Token Wallet Config",
      fieldValue: tokenWallet?.attributes.Name || at.TokenWalletFileConfigName,
    },
    { fieldName: "Note", fieldValue: at.Note },
    {
      fieldName: "Custom Groups",
      fieldValue: at.groups?.length
        ? customGroups
            .filter((group) => at.groups?.includes(group.groupId))
            .map((g) => g.name)
        : undefined,
    },
    {
      fieldName: "Low Value Transaction Limit",
      fieldValue: at.LowValueTransactionLimit
    },
  ];

  const processorItems =
    (at.Processor?.map((prc) => ({
      sectionName: prc.ProcessorName,
      itemData: [
        { fieldName: "MID", fieldValue: prc.MID },
        { fieldName: "Pin Pad Id", fieldValue: prc.PPID },
        { fieldName: "STAN", fieldValue: prc.STAN },
        { fieldName: "TID", fieldValue: prc.TID },
        { fieldName: "Reversal Pending", fieldValue: prc.ReversalPending },
        { fieldName: "Completion Pending", fieldValue: prc.CompletionPending },
      ],

      additionalData: hiddenFields.has('Encrypted') ? _.omit(prc.AdditionalData, 'Encrypted') : prc.AdditionalData,
    })) as ItemDetail[]) || [];

  const terminalProcessors: ItemsDetail = {
    sectionName: "Processor",
    itemsData: filterItemsData(processorItems),
  };

  const terminalApp: ItemsDetail = {
    sectionName: "Applications",
    itemsData:
      (at.Applications?.map((app) => ({
        sectionName: app.AppName,
        itemData: [
          { fieldName: "Version", fieldValue: app.VersionString },
          { fieldName: "Hardware", fieldValue: app.Variant },
        ],
      })) as ItemDetail[]) || [],
  };

  const terminalHosting: Item[] = [
    { fieldName: "URL", fieldValue: at.URL },
    { fieldName: "URL Backup", fieldValue: at.URLBackup },
    { fieldName: "Satellite Upgrade", fieldValue: at.URLUpgrade },
    { fieldName: "Satellite Upgrade Backup", fieldValue: at.URLUpgradeBackup },
    { fieldName: "Terminal Firmware Upgrade", fieldValue: at.URLUpgradeFirmware },
    { fieldName: "URL Upgrade Firmware Backup", fieldValue: at.URLUpgradeFirmwareBackup },
    { fieldName: "Dock Firmware Upgrade", fieldValue: at.DockerFirmware },
    { fieldName: "Docker Firmware Backup", fieldValue: at.DockerFirmwareBackup },
  ];

  const terminalLocation: Item[] = [
    { fieldName: "Location", fieldValue: at.Location },
    { fieldName: "Address 1", fieldValue: at.Address1 },
    { fieldName: "Address 2", fieldValue: at.Address2 },
    { fieldName: "Timezone", fieldValue: at.TZ },
    { fieldName: "Locale", fieldValue: at.Locale },
    { fieldName: "State", fieldValue: at.AddressState },
  ];

  const terminalReceipt: ItemsDetail = {
    sectionName: "Receipt",
    itemsData: [
      {
        sectionName: "Merchant Receipts",
        itemData: [
          { fieldName: "Print Option", fieldValue: at.MerchantReceiptPrint },
          {
            fieldName: "Receipt Template",
            fieldValue:
              at.EnableHTMLReceipts === "No"
                ? at.ReceiptMerchant
                : (at.HTMLReceipts || []).filter((i: string) =>
                    merchantReceipts.includes(i)
                  ),
          },
        ],
      },
      {
        sectionName: "Customer Receipts",
        itemData: [
          { fieldName: "Print Option", fieldValue: at.CustomerReceiptPrint },
          {
            fieldName: "Receipt Template",
            fieldValue:
              at.EnableHTMLReceipts === "No"
                ? at.Receipt
                : (at.HTMLReceipts || []).filter((i: string) =>
                    customerReceipts.includes(i)
                  ),
          },
        ],
      },
      {
        sectionName: "Print Options",
        itemData: [{ fieldName: "Receipt Print", fieldValue: at.ReceiptPrint }],
      },
      {
        sectionName: "Configuration",
        isRTL: true,
        itemData: [
          {
            fieldName: "Show only HTML receipts",
            fieldValue: at.EnableHTMLReceipts,
          },
          {
            fieldName: "Print transaction listing",
            fieldValue: at.PrintTransactionListing,
          },
        ],
      },
    ],
  };
  const preAuthSection = {
    sectionName: "Pre Auth",
    itemData: [
      {
        fieldName: "Pre Auth",
        fieldValue: at.FeaturePreAuth,
      },
      {
        fieldName: "Pre Auth Variance",
        fieldValue:
          at.PreAuth?.VariancePercentage !== null
            ? `${at.PreAuth?.VariancePercentage}%`
            : undefined,
      },
      {
        fieldName: "Pre Auth Expirydays",
        fieldValue:
          at.PreAuth?.ExpiryDays !== null
            ? `${at.PreAuth?.ExpiryDays}%`
            : undefined,
      },
    ],
  };
  const terminalConfiguration: ItemsDetail = {
    sectionName: "Configuration",
    itemsData: [
      {
        sectionName: "Options",
        isRTL: true,
        itemData: [
          { fieldName: "Standalone", fieldValue: displayDefaultValue(at.Standalone, 'configuration') },
          { fieldName: "CloudPOS", fieldValue: displayDefaultValue(at.CloudPOS, 'configuration') },
          { fieldName: "POSApp", fieldValue: displayDefaultValue(at.POSApp, 'configuration') },
          { fieldName: "Cash Out", fieldValue: displayDefaultValue(at.FeatureCashOut, 'configuration') },
          {
            fieldName: "No Cash Out In Debit",
            fieldValue: displayDefaultValue(at.NoCashOutInDebit, 'configuration'),
          },
          {
            fieldName: "Purchase with Cash Out",
            fieldValue: displayDefaultValue(at.FeaturePurWithCash, 'configuration'),
          },
          { fieldName: "Purchase with Tip", fieldValue: displayDefaultValue(at.FeaturePurWithTip, 'configuration') },
          { fieldName: "Refund", fieldValue: displayDefaultValue(at.FeatureRefund, 'configuration') },
          { fieldName: "Pre-Auth", fieldValue: displayDefaultValue(at.FeaturePreAuth, 'configuration') },
          { fieldName: "Completion", fieldValue: displayDefaultValue(at.FeatureCompletion, 'configuration') },
          { fieldName: "MOTO", fieldValue: displayDefaultValue(at.FeatureMOTO, 'configuration') },
          {
            fieldName: "Manual Settlement",
            fieldValue: displayDefaultValue(at.FeatureManualSettlement, 'configuration'),
          },
          { fieldName: "Show Navigation", fieldValue: displayDefaultValue(at.ShowNavigation, 'configuration') },
          {
            fieldName: "Show Last Settlement",
            fieldValue: displayDefaultValue(at.FeatureLastSettle, 'configuration'),
          },
          {
            fieldName: "Pre-Settlement",
            fieldValue: displayDefaultValue(at.FeaturePreSettle, 'configuration'),
          },
          {
            fieldName: "Welfare Restrictions",
            fieldValue: displayDefaultValue(at.WelfareRestriction, 'configuration'),
          },
          {
            fieldName: "Duplicate Transaction Check",
            fieldValue: displayDefaultValue(at.DuplicateTxnCheck, 'configuration'),
          },
          {
            fieldName: "Idle On Amount Entry",
            fieldValue: displayDefaultValue(at.IdleOnAmountEntry, 'configuration'),
          },
          {
            fieldName: "BP Card Support",
            fieldValue: displayDefaultValue(at.BpCardSupport, 'configuration'),
          },
          {
            fieldName: "BP Gift Card Support",
            fieldValue: displayDefaultValue(at.FeatureBPGiftCards, 'configuration'),
          },
          {
            fieldName: "Terminal Status Reporting",
            fieldValue: displayDefaultValue(at.TerminalStatusReportingEnabled, 'configuration'),
          },
          {
            fieldName: "Terminal GPS",
            fieldValue: displayDefaultValue(at.TerminalGPSEnabled, 'configuration'),
          },
          {
            fieldName: "Network Connection Manager",
            fieldValue: displayDefaultValue(at.NetworkConnectionManagerEnabled, 'configuration'),
          },
          {
            fieldName: "Qantas Loyalty Enabled",
            fieldValue: displayDefaultValue(at.FeatureQantasLoyaltyEnabled, 'configuration'),
          },
          {
            fieldName: "Qantas Loyalty Cash Password Protect",
            fieldValue: displayDefaultValue(at.FeatureQantasLoyaltyCashPasswordProtect, 'configuration'),
          },
          {
            fieldName: "Partial Payment",
            fieldValue: displayDefaultValue(at.FeaturePartialPaymentEnabled, 'configuration'),
          },
          {
            fieldName: "MentorLite",
            fieldValue: displayDefaultValue(at.FeatureMentorLite, 'configuration'),
          },
          {
            fieldName: "Lite Theme Enabled",
            fieldValue: displayDefaultValue(at.FeatureLiteThemeEnabled, 'configuration'),
          },
          {
            fieldName: "Crypto.com Enabled",
            fieldValue: displayDefaultValue(at.FeatureCryptoDotComEnabled, 'configuration'),
          },
          {
            fieldName: "Crypto.com Refunds From Terminal Allowed",
            fieldValue: displayDefaultValue(at.FeatureCryptoDotComRefundsEnabled, 'configuration'),
          },
          {
            fieldName: "Pay at Table Enabled",
            fieldValue: displayDefaultValue(at.PayAtTableEnabled, 'configuration'),
          },
          {
            fieldName: "Key Entry Password",
            fieldValue: displayDefaultValue(at.KeyEntryPromptPassword, 'configuration'),
          },
          {
            fieldName: "MOTO Password",
            fieldValue: displayDefaultValue(at.MOTOPromptPassword, 'configuration'),
          },
          {
            fieldName: "Allow Basket Data to Terminal",
            fieldValue: displayDefaultValue(at.AllowBasketData, 'configuration'),
          },
          {
            fieldName: "Taxi Mode Enabled",
            fieldValue: displayDefaultValue(at.FeatureA2BTaxiModeEnabled, 'configuration'),
          },
          {
            fieldName: "Fastcard Enabled",
            fieldValue: displayDefaultValue(at.FeatureCabchargeFastCardEnabled, 'configuration'),
          },
          {
            fieldName: "Enable Self-Installation",
            fieldValue: displayDefaultValue(at.EnableSelfInstall, 'configuration'),
          },
          {
            fieldName: "Stationery Order",
            fieldValue: displayDefaultValue(at.EnableStationeryOrder, 'configuration'),
          },
          {
            fieldName: "Feature Pair With POS Enabled",
            fieldValue: displayDefaultValue(at.FeaturePairWithPOSEnabled, 'configuration'),
          },
          {
            fieldName: "Fleet Card via Fiserv",
            fieldValue: displayDefaultValue(at.FeatureFleetCardAuthCompletion, 'configuration'),
          },
          {
            fieldName: "Show Back Button On Present Card Screen",
            fieldValue: displayDefaultValue(at.ShowBackButtonOnPresentCardScreen, 'configuration'),
          },
          {
            fieldName: "Show Cancel Button On Present Card Screen",
            fieldValue: displayDefaultValue(at.ShowCancelButtonOnPresentCardScreen, 'configuration'),
          },
          {
            fieldName: "FeatureUpdaterEnable",
            fieldValue: displayDefaultValue(at.FeatureUpdaterEnable, 'configuration'),
          },
          {
            fieldName: "Fusion API Logging",
            fieldValue: displayDefaultValue(at.FeatureFusionAPILoggingEnabled, 'configuration'),
          },
          {
            fieldName: "Reversal Managed On Host",
            fieldValue: displayDefaultValue(at.ReversalManagedOnHost, 'configuration'),
          },
          {
            fieldName: "Enable Split Payments",
            fieldValue: displayDefaultValue(at.EnableSplitPayments, 'configuration'),
          },
          {
            fieldName: "Show Status Bar",
            fieldValue: displayDefaultValue(at.ShowStatusBar, 'configuration'),
          },
          {
            fieldName: "USB POS",
            fieldValue: displayDefaultValue(at.USBPOS, 'configuration'),
          },
          {
            fieldName: "Hashed Hot Card Enabled",
            fieldValue: displayDefaultValue(at.HashedHotCardEnabled, 'configuration'),
          },
          {
            fieldName: "Hot Card Delta Download Allowed",
            fieldValue: displayDefaultValue(at.IsHotcardDeltaDownloadAllowed, 'configuration'),
          },
          {
            fieldName: "Enable Feature Shell Contactless",
            fieldValue: displayDefaultValue(at.FeatureShellContactless, 'configuration'),
          },
          {
            fieldName: "Enable Feature PreAuth And Completion",
            fieldValue: displayDefaultValue(at.FeaturePurchaseWithPreAuthAndCompletion, 'configuration'),
          },
        ],
      },
      {
        sectionName: "Payment Methods",
        isRTL: true,
        itemData:
          at.PaymentMethods?.filter((method) => method !== "").map(
            (method) =>
              ({
                fieldName: method,
                fieldValue: method ? "Enable" : "Disabled",
              } as Item)
          ) || [],
      },
      {
        sectionName: "Transit Methods",
        itemData: [
          {
            fieldName: "Transit Mode",
            fieldValue: at.TransitMode,
          },
          {
            fieldName: "Disable Login",
            fieldValue: at.DisableLogin,
          },
          {
            fieldName: "Configure Block List Refresh Intervals (in minutes)",
            fieldValue: at.TransitBlockListRefreshIntervalMs,
          },
        ],
      },
      {
        sectionName: "Qantas",
        itemData: [
          {
            fieldName: "Qantas Loyalty Password",
            fieldValue: at.FeatureQantasLoyaltyCashPassword,
          },
          {
            fieldName: "Qantas Loyalty Logo URL",
            fieldValue: at.QantasLoyaltyLogoUrl,
            isImg: true,
          },
        ],
      },
      {
        sectionName: "Timeouts",
        itemData: [
          {
            fieldName: "CloudPOS timeout (sec)",
            fieldValue: displayDefaultValue(at.CloudPosTimeout, 'timeouts'),
          },
          {
            fieldName: "Inactivity timeout (sec)",
            fieldValue: displayDefaultValue(at.InactivityTimeout, 'timeouts'),
          },
          { fieldName: "Result timeout (ms)", fieldValue: displayDefaultValue(at.ResultTimeout, 'timeouts') },
          { fieldName: "Signature Timeout", fieldValue: displayDefaultValue(at.SignatureTimeOut, 'timeouts') },
          {
            fieldName: "Fallback retry count",
            fieldValue: displayDefaultValue(at.FallbackRetryCount, 'timeouts')
          },
          {
            fieldName: "Terminal Status update interval (sec)",
            fieldValue: displayDefaultValue(at.TerminalStatusReportingInterval, 'timeouts')
          },
          {
            fieldName: "Crypto.com Refund Availability Window (seconds)",
            fieldValue: displayDefaultValue(at.WindowForCDCRefundOptionSeconds, 'timeouts')
          },
          {
            fieldName: "GPS Location update interval (sec)",
            fieldValue: displayDefaultValue(at.TerminalGPSUpdateIntervalSeconds, 'timeouts')
          },
          {
            fieldName: "Screen Dimming Timeout (Ms)",
            fieldValue: displayDefaultValue(at.ScreenDimmingTimeoutMs, 'timeouts')
          },
        ],
      },
      {
        sectionName: "Start up",
        itemData: [{ fieldName: "Startup Mode 1", fieldValue: at.StartupMode }],
      },
      {
        sectionName: "Offline Limits",
        itemData: (at.OfflineLimits || [])
          .map((oc) => {
            const items = [];

            items.push({
              fieldName: "Category",
              fieldValue: oc.Category,
            });
            items.push({
              fieldName: "Product",
              fieldValue: oc.Product,
            });
            items.push({
              fieldName: "Amount",
              fieldValue: oc.Amount,
            });

            return items;
          })
          .flat(),
      },
      {
        sectionName: "Offline Config",
        itemData: [
          {
            fieldName: "Offline Processing",
            fieldValue: at.OfflineConfig?.Enabled,
          },
          {
            fieldName: "Offline First",
            fieldValue: at.OfflineConfig?.OfflineFirst,
          },
          {
            fieldName: "Open Loop Floor Limit",
            fieldValue: at.OfflineConfig?.OpenLoopFloorLimit,
          },
          {
            fieldName: "Close Loop Floor Limit",
            fieldValue: at.OfflineConfig?.CloseLoopFloorLimit,
          },
          { fieldName: "Limit Total", fieldValue: at.OfflineConfig?.LimitTotal },
          { fieldName: "Limit Count", fieldValue: at.OfflineConfig?.LimitCount },
        ],
      },
      {
        sectionName: "Merchant Choice Routing (MCR)",
        itemData: [
          {
            fieldName: "MCR",
            fieldValue: !isNaN(at.MCRMinAmount)
              ? centToDollar(at.MCRMinAmount)
              : undefined,
          },
          {
            fieldName: "MCR Mastercard",
            fieldValue: !isNaN(at.MastercardMCRThreshold)
              ? centToDollar(at.MastercardMCRThreshold)
              : undefined,
          },
          {
            fieldName: "MCR Visa",
            fieldValue: !isNaN(at.VisaMCRThreshold)
              ? centToDollar(at.VisaMCRThreshold)
              : undefined,
          },
        ],
      },
      {
        sectionName: "Others",
        itemData: [
          { fieldName: "Medium Priority", fieldValue: at.MediumPriority },
          { fieldName: "Terminal State", fieldValue: at.State },
          { fieldName: "Temp Password", fieldValue: at.TempPassword },
          { fieldName: "Background Logo", fieldValue: at.IdleBitmap as string },
          {
            fieldName: "Refund Transaction Limit",
            fieldValue: !isNaN(at.FeatureRefundLimit)
              ? centToDollar(at.FeatureRefundLimit)
              : undefined,
          },
          {
            fieldName: "Refund Daily Limit",
            fieldValue: !isNaN(at.FeatureRefundDailyLimit)
              ? centToDollar(at.FeatureRefundDailyLimit)
              : undefined,
          },
          {
            fieldName: "Battery Limit",
            fieldValue: at.BatteryLimit,
          },
          {
            fieldName: "Auto Software/Firmware Update Enabled",
            fieldValue: at.AutoSoftwareUpdateEnabled,
          },
          {
            fieldName: "Auto Update Time From",
            fieldValue: convertTimeTo12Hours(at.AutoUpdateTimeFrom as string),
          },
          {
            fieldName: "Auto Update Time To",
            fieldValue: convertTimeTo12Hours(at.AutoUpdateTimeTo as string),
          },
          {
            fieldName: "Enable Self-Installation",
            fieldValue: at.EnableSelfInstall,
          },
          {
            fieldName: "Pin Retry Attempt Count",
            fieldValue: at.PinRetryAttemptCount,
          },
        ],
      },
    ],
  };
  if (at.TerminalType === "QT 850") {
    terminalConfiguration.itemsData.splice(1, 0, preAuthSection);
  }

  const offlineProcessing: Item[] = [
    {
      fieldName: "Offline processing",
      fieldValue: at.OfflineConfig?.Enabled,
    },
    {
      fieldName: "Offline first",
      fieldValue: at.OfflineConfig?.OfflineFirst,
    },
    {
      fieldName: "Open Loop Floor Limit",
      fieldValue: at.OfflineConfig?.OpenLoopFloorLimit,
    },
    {
      fieldName: "Close Loop Floor Limit",
      fieldValue: at.OfflineConfig?.CloseLoopFloorLimit,
    },
    {
      fieldName: "Limit Total",
      fieldValue: at.OfflineConfig?.LimitTotal,
    },
    {
      fieldName: "Limit Count",
      fieldValue: at.OfflineConfig?.LimitCount,
    },
  ];

  const terminalSurchargeTips: ItemsDetail = {
    sectionName: "Surcharge & Tips",
    itemsData: [
      {
        sectionName: "Surcharge Version",
        itemData: [
          {
            fieldName: "Surcharge Version",
            fieldValue: at.SurchargeVersion,
          },
        ],
      },
      {
        sectionName: "Fixed Surcharge Rate",
        itemData: [
          {
            fieldName: "Percentage",
            fieldValue: at.FixedSurchargePercentage,
          },
          {
            fieldName: "Amount",
            fieldValue: at.FixedSurchargeAmount,
          },
        ],
      },
      {
        sectionName: "Surcharge V1 Toggles",
        itemData: [
          {
            fieldName: "Surcharge",
            fieldValue: at.Surcharge,
          },
          {
            fieldName: "Surcharge on tips",
            fieldValue: at.SurchargeTip,
          },
          {
            fieldName: "GST on surcharge",
            fieldValue: at.SurchargeGST,
          },
        ],
      },
      {
        sectionName: "Surcharge V1 Card Types",
        itemData: (at.SurchargePercentage || [])
          .map((per) => {
            const items = [];

            items.push({ fieldName: "Card Type", fieldValue: per.CardType });
            items.push({
              fieldName: "Surcharge",
              fieldValue: per.Percentage.toString() + "%",
            });
            items.push({
              fieldName: "GST on Surcharge",
              fieldValue: per.SurchargeGST,
            });
            return items;
          })
          .flat(),
      },
      {
        sectionName: "Surcharge V2 Toggles",
        itemData: [
          {
            fieldName: "Enable surcharge",
            fieldValue: at.EnableSurchargeV2,
          },
          {
            fieldName: "Surcharge on tips",
            fieldValue: at.SurchargeTip,
          },
          {
            fieldName: "GST on surcharge",
            fieldValue: at.SurchargeGST,
          },
          {
            fieldName: "Enable surcharge settings on terminal",
            fieldValue: at.EnableSurchargeSettingsOnTerminal,
          },
        ],
      },
      {
        sectionName: "Surcharge V2 Card Types",
        itemData: (at.SurchargeV2 || [])
          .map((sur) => {
            const items = [];

            items.push({ fieldName: "Card Type", fieldValue: sur.CardType });
            items.push({
              fieldName: "Surcharge Type",
              fieldValue: sur.SurchargeType,
            });
            items.push({
              fieldName: "Purchase Surcharge",
              fieldValue: sur.PurchaseSurchargeValue,
            });
            items.push({
              fieldName: "Cash Out Surcharge",
              fieldValue: sur.CashoutSurchargeValue,
            });
            items.push({
              fieldName: "GST",
              fieldValue: sur.GSTValue,
            });
            return items;
          })
          .flat(),
      },
      {
        sectionName: "Tipping V1",
        itemData: [
          { fieldName: "Tipping Mode", fieldValue: at.TippingMode },
          {
            fieldName: "Value 1",
            fieldValue: formatTippingPercentageValue(
              at.TippingMode as string,
              at.TipWithPercentagePercentage1 as number
            ),
          },
          {
            fieldName: "Value 2",
            fieldValue: formatTippingPercentageValue(
              at.TippingMode as string,
              at.TipWithPercentagePercentage2 as number
            ),
          },
          {
            fieldName: "Value 3",
            fieldValue: formatTippingPercentageValue(
              at.TippingMode as string,
              at.TipWithPercentagePercentage3 as number
            ),
          },
        ],
      },
      {
        sectionName: "Tipping V2",
        itemData: [
          { fieldName: "Tipping Mode", fieldValue: at.TippingV2?.TippingMode },
          {
            fieldName: "Value 1",
            fieldValue: formatTippingPercentageValue(
              at.TippingV2?.TippingMode as string,
              at.TippingV2?.TipWithPercentage1 as number
            ),
          },
          {
            fieldName: "Value 2",
            fieldValue: formatTippingPercentageValue(
              at.TippingV2?.TippingMode as string,
              at.TippingV2?.TipWithPercentage2 as number
            ),
          },
          {
            fieldName: "Value 3",
            fieldValue: formatTippingPercentageValue(
              at.TippingV2?.TippingMode as string,
              at.TippingV2?.TipWithPercentage3 as number
            ),
          },
        ],
      },
    ],
  };

  const terminalKeys: Item[] = [
    {
      fieldName: "Terminal Master Key",
      fieldValue: !isNil(at.TMK1 || at.TMK2),
    },
    {
      fieldName: "Session Keys",
      fieldValue: isNil(at.TPK || at.TAKs || at.TAKr || at.TEKs || at.TEKr),
    },
    { fieldName: "Manufacturer", fieldValue: at.Manufacturer },
    { fieldName: "Model", fieldValue: at.Model },
    { fieldName: "Model Extended", fieldValue: at.ModelExtended },
    { fieldName: "Installed Terminal Firmware", fieldValue: at.OS },
    { fieldName: "Dock Firmware Version", fieldValue: at.InstalledDockerFirmware },
    { fieldName: "Crypto Date", fieldValue: at.CryptoID },
    { fieldName: "Pin Pad ID", fieldValue: at.PPID },
    { fieldName: "AXISPayVersion", fieldValue: at.AXISPayVersion },
    { fieldName: "STAN", fieldValue: at.STAN },
    { fieldName: "Temp Password", fieldValue: at.TempPassword },
  ];

  const terminalGiftCard: Item[] = [
    {
      fieldName: "Username",
      fieldValue: at.BPGiftCardsConfig?.Username,
    },
    {
      fieldName: "Password",
      fieldValue: at.BPGiftCardsConfig?.Password,
    },
    {
      fieldName: "Legacy Password",
      fieldValue: at.BPGiftCardsConfig?.LegacyPassword,
    },
    {
      fieldName: "Legacy Username",
      fieldValue: at.BPGiftCardsConfig?.LegacyUsername,
    },
    {
      fieldName: "Shop ID",
      fieldValue: at.BPGiftCardsConfig?.ShopId,
    },
    {
      fieldName: "Till ID",
      fieldValue: at.BPGiftCardsConfig?.TillId,
    },
    {
      fieldName: "Terminal ID",
      fieldValue: at.BPGiftCardsConfig?.TerminalId,
    },
    {
      fieldName: "Legacy Terminal ID",
      fieldValue: at.BPGiftCardsConfig?.LegacyTerminalId,
    },
    {
      fieldName: "Currency",
      fieldValue: at.BPGiftCardsConfig?.Currency,
    },
  ];

  const terminalPayAtTable: Item[] = [
    {
      fieldName: "Host Mode",
      fieldValue: at.PayAtTable?.HostMode,
    },
    {
      fieldName: "Host URL",
      fieldValue: at.PayAtTable?.HostURL as string,
    },
    {
      fieldName: "Merchant Password",
      fieldValue: at.PayAtTable?.MerchantPassword as string,
    },
    {
      fieldName: "Store Number",
      fieldValue: at.PayAtTable?.StoreNumber as string,
    },
    {
      fieldName: "User Number",
      fieldValue: at.PayAtTable?.UserNumber as string,
    },
    { fieldName: "Client ID", fieldValue: at.PayAtTable?.ClientId as string },
    {
      fieldName: "Client Secret",
      fieldValue: at.PayAtTable?.ClientSecret as string,
    },
    {
      fieldName: "Token Host Endpoint",
      fieldValue: at.PayAtTable?.TokenHostEndpoint as string,
    },
    {
      fieldName: "Token Host Audience",
      fieldValue: at.PayAtTable?.TokenHostAudience as string,
    },
    {
      fieldName: "Print Merchant Receipt",
      fieldValue: at.PayAtTable?.PrintMerchantReceipt,
    },
    {
      fieldName: "Enable Split Payment",
      fieldValue: at.PayAtTable?.EnableSplitPayment,
    },
  ];

  const tabsInScroll = [
          {
            label: "Terminal Info",
            component: (
              <ItemDetailViewSection
                sectionName="Terminal Info"
                itemData={filterItems(terminalInfo)}
                isRestricted={!TerminalInfoAccess.R}
              />
            ),
          },
          {
            label: "Processors",
            component: (
              <ItemsDetailViewSection
                sectionName={terminalProcessors.sectionName}
                itemsData={terminalProcessors.itemsData}
                isRestricted={!ProcessorAccess.R}
              />
            ),
          },
          {
            label: "Terminal Update & Connectivity",
            component: (
              <ItemDetailViewSection
                sectionName="Terminal Update & Connectivity ( Hosting )"
                itemData={filterItems(terminalHosting)}
                isRestricted={!HostingAccess.R}
              />
            ),
          },
          {
            label: "Applications",
            component: (
              <ItemsDetailViewSection
                sectionName={terminalApp.sectionName}
                itemsData={terminalApp.itemsData}
                isRestricted={!HostingAccess.R}
              />
            ),
          },
          {
            label: "Location",
            component: (
              <ItemDetailViewSection
                sectionName="Location"
                itemData={terminalLocation}
                isRestricted={!LocationAccess.R}
              />
            ),
          },
          {
            label: "Receipt",
            component: (
              <ItemsDetailViewSection
                sectionName={terminalReceipt.sectionName}
                itemsData={filterItemsData(terminalReceipt.itemsData)}
                isColumn={true}
                isRestricted={!ReceiptAccess.R}
              />
            ),
          },
          {
            //create tooltip object and insert it into ItemsDetailViewSection as terminalConfiguration.tooltipTitle?
            label: "Configuration",
            component: (
              <ItemsDetailConfigurationViewSection
                sectionName={terminalConfiguration.sectionName}
                itemsData={filterItemsData(terminalConfiguration.itemsData)}
                isColumn={true}
                isRestricted={!ConfigurationAccess.R}
              />
            ),
          },
          {
            label: "Offline Processing",
            component: (
              <ItemDetailViewSection
                sectionName="Offline Processing"
                itemData={offlineProcessing}
                isRestricted={!OfflineProcessingAccess.R}
              />
            ),
          },
          ...(!isHideSurchargeSection
            ? [
                {
                  label: "Surcharge & Tips",
                  component: (
                    <ItemsDetailViewSection
                      sectionName={terminalSurchargeTips.sectionName}
                      col={2}
                      itemsData={filterItemsData(terminalSurchargeTips.itemsData)}
                      isSurcharge={true}
                      isRestricted={!SurchargeTippingAccess.R}
                    />
                  ),
                },
              ]
            : []),
          ...(at.FeatureBPGiftCards === "Yes"
            ? [
                {
                  label: "Gift Card",
                  component: (
                    <ItemDetailViewSection
                      sectionName="Gift Card"
                      itemData={terminalGiftCard}
                      isRestricted={!GiftCardAccess.R}
                    />
                  ),
                },
              ]
            : []),
          ...(at.PayAtTableEnabled === "Yes"
            ? [
                {
                  label: "PayAtTable",
                  component: (
                    <ItemDetailViewSection
                      sectionName="PayAtTable"
                      itemData={terminalPayAtTable}
                      isRestricted={!PayAtTableAccess.R}
                    />
                  ),
                },
              ]
            : []),
          ...(at.FeatureDRCLoyaltyEnabled === "Yes"
            ? [
                {
                  label: "Dining Rewards Club",
                  component: (
                    <ItemDetailViewSection
                      sectionName="Dining Rewards Club"
                      itemData={[
                        {
                          fieldName: "Minimum Loyalty Amount",
                          fieldValue: at.DRCMinLoyaltyAmount,
                        },
                        { fieldName: "Store ID", fieldValue: at.DRCStoreId },
                        {
                          fieldName: "Loyalty Logo Url",
                          fieldValue: at.DRCLoyaltyLogoUrl,
                        },
                        {
                          fieldName: "Loyalty Cash Password",
                          fieldValue: at.FeatureDRCLoyaltyCashPassword,
                        },
                        {
                          fieldName: "DRC Cash Password Protect",
                          fieldValue: at.FeatureDRCLoyaltyCashPasswordProtect,
                        },
                      ]}
                      isRestricted={!DiningRewardsClubAccess.R}
                    />
                  ),
                },
              ]
            : []),
          {
            label: "Terminal Detail",
            component: (
              <ItemDetailViewSection
                sectionName="Terminal Detail"
                itemData={filterItems(terminalKeys)}
                lastItem
                isRestricted={!TerminalKeyAccess.R}
              />
            ),
          },
        ];

  const visibleTabs =
    hiddenTabs.size === 0
      ? tabsInScroll
      : tabsInScroll.filter((t) => !hiddenTabs.has(t.label));

  return (
    <Box>
      <ScrollSpyTabs tabsInScroll={visibleTabs} />
    </Box>
  );
};

export default TerminalViewDetail;
